// ─────────────────────────────────────────────────────────────────────────────
// Packaging design tokens — the "Rémi" design system, packaging edition.
//
// This is the single source of truth for the packaging-v3 UI chrome (sidebar,
// top bar, dashboard, cards). It reconciles the approved mockup
// (`claude/blueprints/packaging-dashboard.html`) with the Unitag design system
// documented in `dashboard/claude/claude-design.md`.
//
// Rules of the road:
//  - The brand green is canonical (#158F48) but, wherever an element marks the
//    *primary* / *active* state, read it from the MUI theme (`primary.main`) so
//    the white-label palette (`dash_design`) still applies. The green token here
//    is only for static accents/illustrations that never recolour per account.
//  - Neutrals, radii and shadows are the one scale — do not invent new values in
//    components; add them here first.
// ─────────────────────────────────────────────────────────────────────────────

export const packagingTokens = {
    color: {
        // Brand (canonical — prefer theme.primary for active/primary states)
        green: "#158F48",
        greenDark: "#0D5C2E",
        greenLight: "#1DAF5A",
        green100: "#E8F7EE",
        green50: "#F2FBF5",

        // Surfaces & background
        // bg mirrors the dashboard's `palette.pageBackground` (designTokens.ts)
        // so the iframe blends seamlessly into the host page — keep in sync.
        bg: "#F7F8F1",
        surface: "#FFFFFF",

        // Borders (hairlines)
        border: "#E5E7EB",
        borderLight: "#F0F1F3",
        borderStrong: "#D1D5DB",

        // Text
        textPrimary: "#111318",
        textSecondary: "#5F6B7A",
        textTertiary: "#9CA3AF",

        // Status / accent
        red: "#EF4444",
        red100: "#FEE2E2",
        amber: "#F59E0B",
        amber100: "#FEF3C7",
        blue: "#3B82F6",
        blue100: "#DBEAFE",
        purple: "#7C3AED",
        purple100: "#F3E8FF",
        // Dev-only UI (matches the dashboard's `palette.dev` orange) — marks
        // local-development controls that never ship to sandbox/production.
        dev: "#f37b05",
    },

    radius: {
        sm: "6px",
        // The Rémi surface standard — all cards / papers / panels share 8px
        // (the single "REMI_RADIUS" knob from the design system).
        card: "8px",
        // Form controls (inputs, selects, autocompletes) + menus/popovers share
        // the same 8px knob — applied theme-wide in PackagingUiProvider so every
        // control matches the dashboard (claude-design.md §3.8).
        control: "8px",
        md: "10px",
        lg: "14px",
        xl: "18px",
        pill: "100px",
    },

    shadow: {
        sm: "0 1px 2px rgba(0,0,0,0.04)",
        md: "0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
        lg: "0 8px 24px rgba(0,0,0,0.08)",
        card: "0 1px 3px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)",
    },

    layout: {
        sidebarWidth: 260,
        headerHeight: 56,
        contentMaxWidth: 1320,
        contentPadding: 32,
    },

    font: {
        family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        mono: "'JetBrains Mono', ui-monospace, monospace",
    },
} as const

export type PackagingTokens = typeof packagingTokens
